import axios, { type AxiosError } from 'axios'
import {
    useMutation,
    useQueryClient,
} from '@tanstack/react-query'
import dayjs, { type Dayjs } from 'dayjs'

import type {
    FormMutationOptions,
    MutationOptions,
    YearlyFeePaymentFormInput,
} from '@/types'
import { convertToCents, isErrorResponse } from '@/helpers'

export const useCreateYearlyFeePayment = (feePayerId: number | string, options: FormMutationOptions) => {
    const queryClient = useQueryClient()

    const {
        onSuccessCallback,
        formDataId,
        setRequestInProgress,
    } = options

    return useMutation({
        mutationFn: (data: YearlyFeePaymentFormInput) => {            
            const queryParams = new URLSearchParams()
            queryParams.append('fee_payer_id', feePayerId.toString())
            queryParams.append('requested_amount_cents', data.requested_amount_cents ? convertToCents(data.requested_amount_cents).toString() : '')
            if (data.due_date) {
                queryParams.append('due_date', dayjs(data.due_date).endOf('day').format('YYYYMMDD HH:mm'))
            }

            return axios.post(`/api/yearly_fee_payment/create?${queryParams.toString()}`)
        },
        onSuccess: response => {
            queryClient.invalidateQueries({ queryKey: ['yearlyFeePayments'] })
            queryClient.invalidateQueries({ queryKey: ['yearlyFeePayer', feePayerId] })
            localStorage.removeItem(formDataId)
            if (onSuccessCallback) {
                onSuccessCallback(response.data)
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        }
    })
}

export const useUpdateYearlyFeePayment = (yearlyFeePaymentId, options: FormMutationOptions) => {
    const queryClient = useQueryClient()

    const {
        onSuccessCallback,
        formDataId,
        setRequestInProgress,
        onErrorCallback,
    } = options

    return useMutation({
        mutationFn: (data: YearlyFeePaymentFormInput) => {
             const queryParams = new URLSearchParams()
            queryParams.append('id', yearlyFeePaymentId.toString())
            queryParams.append('requested_amount_cents', data.requested_amount_cents ? convertToCents(data.requested_amount_cents).toString() : '')
            if (data.due_date) {
                queryParams.append('due_date', dayjs(data.due_date).endOf('day').format('YYYYMMDD HH:mm'))
            }

            return axios.put(`/api/yearly_fee_payment/update?${queryParams.toString()}`)
        },
        onSuccess: response => {
            queryClient.invalidateQueries({ queryKey: ['yearlyFeePayments'] })
            queryClient.invalidateQueries({ queryKey: ['yearlyFeePayer'] })
            localStorage.removeItem(formDataId)
            if (onSuccessCallback) {
                onSuccessCallback(response.data)
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
        onError: (axiosResponse: AxiosError) => {
            const errorData = axiosResponse.response?.data
            if (onErrorCallback){
                if (isErrorResponse(errorData)) {
                    onErrorCallback(errorData.error)
                } else {
                    console.error(axiosResponse)
                    onErrorCallback('Došlo k neočekávané chybě.');
                }
            }
        }
    })
}

export const useRecordYearlyFeePayment = (yearlyFeePaymentId, options: MutationOptions) => {
    const queryClient = useQueryClient()

    const {
        onSuccessCallback,
        setRequestInProgress,
        onErrorCallback,
    } = options

    return useMutation({
        mutationFn: (paid_at: string | Dayjs) => {
             const queryParams = new URLSearchParams()
            queryParams.append('id', yearlyFeePaymentId.toString())
            queryParams.append('paid_at', dayjs(paid_at).endOf('day').format('YYYYMMDD HH:mm'))

            return axios.post(`/api/yearly_fee_payment/record_payment?${queryParams.toString()}`)
        },
        onSuccess: response => {
            queryClient.invalidateQueries({ queryKey: ['yearlyFeePayment', yearlyFeePaymentId] })
            queryClient.invalidateQueries({ queryKey: ['yearlyFeePayer'] })
            const data = response.data
            if (!data.errors || data.errors.length === 0) {
                onSuccessCallback('Email odeslán.')
            } else {
                onErrorCallback(data.errors.join(', '))
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
        onError: (axiosResponse: AxiosError) => {
            const errorData = axiosResponse.response?.data
            if (onErrorCallback){
                if (isErrorResponse(errorData)) {
                    onErrorCallback(errorData.error)
                } else {
                    console.error(axiosResponse)
                    onErrorCallback('Došlo k neočekávané chybě.');
                }
            }
        }
    })
}

export const useDeleteYearlyFeePayment = (yearlyFeePaymentId: number, municipalityId: number, options: MutationOptions,) => {
    const queryClient = useQueryClient()

    const {
        onSuccessCallback,
        setRequestInProgress,
    } = options

    return useMutation({
        mutationFn: () => {
            return axios.delete(`/api/yearly_fee_payment/destroy?id=${yearlyFeePaymentId}`)
        },
        onSuccess: response => {
            queryClient.invalidateQueries({ queryKey: ['yearlyFeePayments', municipalityId] })
            queryClient.invalidateQueries({ queryKey: ['yearlyFeePayer'] })
            if (onSuccessCallback) {
                onSuccessCallback(response.data)
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        }
    })
}

export const useSendPaymentRequestEmail = (yearlyFeePaymentId, options: MutationOptions) => {

    const {
        setRequestInProgress,
        onErrorCallback,
        onSuccessCallback,
    } = options

    return useMutation({
        mutationFn: () => {
            return axios.post(`/api/yearly_fee_payment/send_payment_request_email?id=${yearlyFeePaymentId}`)
        },
        onSuccess: response => {
            const data = response.data
            if (data.errors.length === 0) {
                onSuccessCallback('Email odeslán.')
            } else {
                onErrorCallback(data.errors.join(', '))
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
        onError: (axiosResponse: AxiosError) => {
            const errorData = axiosResponse.response?.data
            if (onErrorCallback){
                if (isErrorResponse(errorData)) {
                    onErrorCallback(errorData.error)
                } else {
                    console.error(axiosResponse)
                    onErrorCallback('Došlo k neočekávané chybě.');
                }
            }
        }
    })
}
